import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { MODEL_TYPES } from '@/config/models';

/**
 * DashboardPage - Model selection page
 * Replicates the design of public/dashboard.html
 */
export function DashboardPage() {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter models based on search query
  const filteredModels = useMemo(() => {
    if (!searchQuery.trim()) return MODEL_TYPES;

    const query = searchQuery.toLowerCase();
    return MODEL_TYPES.filter(
      (model) =>
        model.name.toLowerCase().includes(query) ||
        model.description.toLowerCase().includes(query) ||
        model.id.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Model card component with tags
  const modelTags: Record<string, string[]> = {
    saas: ['MRR', 'Churn', 'LTV'],
    ecommerce: ['AOV', 'Traffic', 'CAC'],
    restaurant: ['Covers', 'Turns', 'Mix'],
    marketplace: ['GMV', 'Take Rate', 'Network'],
    manufacturing: ['Capacity', 'Yield', 'Inventory'],
    consulting: ['Utilization', 'Rates', 'Retainers'],
    retail: ['Foot Traffic', 'Conversion', 'SKUs'],
    cloudkitchen: ['Orders', 'Platform', 'Delivery'],
    fintech: ['TPV', 'Take Rate', 'Float'],
    healthcare: ['Patients', 'Insurance', 'Clinics'],
  };

  // New model badges
  const newModels = ['fintech', 'healthcare'];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased">
      <Navbar activeLink="dashboard" />

      {/* Header */}
      <section className="bg-white border-b border-slate-200 pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 text-slate-900 tracking-tight">
              Choose Your Business Model
            </h1>
            <p className="text-xl text-slate-600 mb-2 max-w-3xl mx-auto">
              Professional financial modeling platform with industry-specific
              assumptions.
            </p>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-8">
              Create detailed financial projections with balanced statements
              ready for investors.
            </p>

            {/* Search Bar */}
            <div className="max-w-md mx-auto relative">
              <svg
                className="absolute left-4 top-3.5 w-5 h-5 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search business models (e.g., SaaS, Retail)..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all shadow-sm text-slate-600 placeholder:text-slate-400"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Business Models Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 -mt-12">
            {filteredModels.map((model, index) => (
              <motion.div
                key={model.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/model/${model.id}`}
                  className="block bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-primary-300 group h-full flex flex-col relative overflow-hidden"
                >
                  {/* NEW Badge for new models */}
                  {newModels.includes(model.id) && (
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-blue-50 text-blue-700 text-[10px] font-bold px-2 py-1 rounded-full border border-blue-100 shadow-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                      NEW
                    </div>
                  )}

                  {/* Icon */}
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl mb-6 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${model.color}15` }}
                  >
                    {model.icon}
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    {model.name}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-500 mb-6 text-sm leading-relaxed flex-grow">
                    {model.description}
                  </p>

                  {/* Tags */}
                  <div className="bg-slate-50 rounded-lg p-3 mb-6 border border-slate-100">
                    <div className="flex flex-wrap gap-2">
                      {(modelTags[model.id] || []).map((tag) => (
                        <span
                          key={tag}
                          className="bg-white border border-slate-200 text-slate-600 px-2 py-1 rounded text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Button */}
                  <button className="w-full bg-white text-slate-700 border border-slate-200 py-3 rounded-lg font-semibold hover:bg-slate-50 hover:border-slate-300 transition-colors mt-auto group-hover:bg-primary-50 group-hover:text-primary-700 group-hover:border-primary-200">
                    Build {model.name} Model
                  </button>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* No results */}
          {filteredModels.length === 0 && (
            <motion.div
              className="text-center py-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                No models found
              </h3>
              <p className="text-slate-500">
                Try a different search term or{' '}
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-primary-600 hover:underline"
                >
                  clear the search
                </button>
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="font-bold text-slate-900 mb-2">
                5-Year Projections
              </h3>
              <p className="text-slate-600 text-sm">
                Detailed year-by-year forecasts with monthly breakdowns for
                Year 1.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl mb-4">⚖️</div>
              <h3 className="font-bold text-slate-900 mb-2">
                Balanced Statements
              </h3>
              <p className="text-slate-600 text-sm">
                P&L, Balance Sheet, and Cash Flow that always tie out
                perfectly.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl mb-4">📥</div>
              <h3 className="font-bold text-slate-900 mb-2">
                Excel Export
              </h3>
              <p className="text-slate-600 text-sm">
                Download fully editable .xlsx files with all formulas intact.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
